import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "./api-client";
import { ACCOUNTS_KEY } from "./accounts";

export type QrLoginEvent =
    | { type: "qr"; imageBase64: string }
    | { type: "expired" }
    | { type: "scanned"; displayName: string; avatarUrl: string }
    | { type: "declined" }
    | { type: "success"; accountId: string; displayName: string; isNew: boolean }
    | { type: "error"; message: string };

export type QrLoginState = {
    phase: "idle" | "starting" | "waiting" | "scanned" | "expired" | "declined" | "success" | "error";
    qrImage?: string;
    scannedName?: string;
    scannedAvatar?: string;
    message?: string;
};

/** Mở luồng quét QR và nghe sự kiện qua SSE. */
export function useQrLogin() {
    const queryClient = useQueryClient();
    const [state, setState] = useState<QrLoginState>({ phase: "idle" });
    const sourceRef = useRef<EventSource | null>(null);
    const sessionRef = useRef<string | null>(null);

    const cleanup = useCallback(() => {
        sourceRef.current?.close();
        sourceRef.current = null;
    }, []);

    const cancel = useCallback(() => {
        cleanup();
        const sessionId = sessionRef.current;
        sessionRef.current = null;
        setState({ phase: "idle" });
        if (sessionId) void api.delete(`/api/accounts/qr-login/${sessionId}`).catch(() => undefined);
    }, [cleanup]);

    const start = useCallback(async () => {
        cleanup();
        setState({ phase: "starting" });

        try {
            const { sessionId } = await api.post<{ sessionId: string }>("/api/accounts/qr-login");
            sessionRef.current = sessionId;

            const source = new EventSource(`/api/accounts/qr-login/${sessionId}/stream`);
            sourceRef.current = source;

            source.onmessage = (message) => {
                const event = JSON.parse(message.data as string) as QrLoginEvent;

                switch (event.type) {
                    case "qr":
                        setState({ phase: "waiting", qrImage: event.imageBase64 });
                        break;
                    case "scanned":
                        setState((prev) => ({
                            ...prev,
                            phase: "scanned",
                            scannedName: event.displayName,
                            scannedAvatar: event.avatarUrl,
                        }));
                        break;
                    case "expired":
                        setState({ phase: "expired", message: "Mã QR đã hết hạn" });
                        break;
                    case "declined":
                        setState({ phase: "declined", message: "Bạn đã từ chối đăng nhập trên điện thoại" });
                        break;
                    case "success":
                        setState({ phase: "success", message: event.displayName });
                        void queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
                        cleanup();
                        break;
                    case "error":
                        setState({ phase: "error", message: event.message });
                        cleanup();
                        break;
                }
            };

            source.onerror = () => {
                setState((prev) =>
                    prev.phase === "success" ? prev : { phase: "error", message: "Mất kết nối tới máy chủ" },
                );
                cleanup();
            };
        } catch (error) {
            setState({ phase: "error", message: error instanceof Error ? error.message : "Không mở được phiên quét" });
        }
    }, [cleanup, queryClient]);

    useEffect(() => cleanup, [cleanup]);

    return { state, start, cancel };
}
